import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import { AsyncSearchableSelect, DepartmentIcon, OptionType, themeContext, UserAvatar } from "shared";
import {
    requestStaffPeople,
    requestStaffDepartment,
    getUserByLogin,
    getDepartmentByCode
} from "shared/lib/utils/suggest-staff-api";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { UserSearchResult, userTypes } from "../../lib/users-service-types";
import styles from "./user-search-select.module.scss";

interface IUserSearchSelectProps {
    value?: OptionType | OptionType[] | null;
    disabled?: boolean;
    placeholder?: string;
    isMulti?: boolean;
    includeAll?: boolean;
    excludeUsers?: UserSearchResult[];
    showSelected?: boolean;
    name?: string;
    onChange?: (value: UserSearchResult | UserSearchResult[] | null) => void;
}

export const renderAvatar = (login: string, theme?: any, size: "s" | "m" = "s") => {
    return <UserAvatar
        icon={`https://center.yandex-team.ru/api/v1/user/${login}/avatar/85.jpg`}
        staffCardParams={{ login }}
        dismissed={false}
        theme={theme}
        size={size}
    />;
};

export const renderGroupIcon = () => {
    return <div className={styles.departmentIconContainer}>
        <DepartmentIcon sizeY={22} sizeX={22}/>
    </div>;
};

export const UserSearchSelect: React.FC<IUserSearchSelectProps> = ({
                                                                       value,
                                                                       name,
                                                                       placeholder = "",
                                                                       includeAll = false,
                                                                       excludeUsers = [],
                                                                       disabled = false,
                                                                       showSelected = true,
                                                                       isMulti = false,
                                                                       onChange,
                                                                   }) => {
    const [selectValue, setSelectValue] = useState<OptionType | OptionType[] | null>();
    const theme = useContext(themeContext);
    const { i18n } = useTranslation();

    useEffect(() => {
        if (value) {
            setSelectValue(value);
        }
    }, [value]);

    const formatOptionLabel = (option: OptionType) => {
        return <div className={cn(styles.options)}>
            {option.type === userTypes.person && renderAvatar(option.value, theme)}
            {option.type !== userTypes.person && renderGroupIcon()}
            <div className={cn(styles.optionElement)}>
                <div className={cn(styles.optionName)}>
                    <div>{option.label}</div>
                    {option.value !== "ALL" && option.type === userTypes.person && <div>({option.value})</div>}
                </div>
                {option.department && option.type === userTypes.person && <div className={cn(styles.departmentName)}>
                    {option.department}
                </div>}
            </div>
        </div>;
    };

    const loadOptions = debounce((inputValue: string, callback: (options: OptionType[]) => void) => {
        let departments: OptionType[] = [];
        let users: OptionType[] = [];

        if (!!inputValue) {
            const departmentsRes = requestStaffDepartment(inputValue, i18n.language)
                .then((response) => {
                    if (response?.groups?.result) {
                        departments = response.groups.result
                            .map((dep: any) => ({
                                value: dep.slug,
                                label: dep.title,
                                type: userTypes.group,
                            }));
                    }
                });

            const usersRes = requestStaffPeople(inputValue, i18n.language)
                .then((response) => {
                    if (response?.people?.result) {
                        users = response.people.result
                            .map((p: any) => ({
                                value: p.login,
                                label: `${p.name.first} ${p.name.last}`,
                                department: p["department_name"],
                                type: userTypes.person
                            }));
                    }
                });
            const promises = [departmentsRes, usersRes];

            Promise.all(promises)
                .then(() => {
                    const results = [...departments, ...users];

                    if (includeAll) {
                        results.unshift({
                            value: "ALL",
                            label: i18n.language === "en" ? "All" : "Все",
                            type: userTypes.all
                        });
                    }

                    if (results.length > 0) {
                        callback(results.filter((p) => !excludeUsers.map((u) => u.login).includes(p.value)));
                    } else {
                        callback([]);
                    }
                })
                .catch(() => {
                    callback([]);
                    toast.error("suggestError");
                });
        } else {
            callback([]);
        }
    }, 200);

    function handleChange(val: any) {
        showSelected && setSelectValue(val);
        if (onChange) {
            if (Array.isArray(val)) {
                onChange(val.map((option: OptionType) => {
                    return {
                        login: option.value,
                        name: option.label,
                        nameEn: option.label,
                        type: option.type,
                        department: option.department,
                    };
                }));
            } else {
                const oppositeLang = i18n.language === "ru" ? "en" : "ru";
                switch (val.type) {
                    case userTypes.person:
                        getUserByLogin(val.value, oppositeLang).then(res => {
                            let oppositeName = "";
                            if (res && res.people && res.people.result) {
                                const oppositeVal = res.people.result.find((r) => r.login === val.value);
                                if (oppositeVal) {
                                    oppositeName = `${oppositeVal.name.first} ${oppositeVal.name.last}`;
                                }
                            }
                            onChange({
                                login: val.value,
                                name: oppositeLang === "en" ? val.label : oppositeName,
                                nameEn: oppositeLang === "en" ? oppositeName : val.label,
                                type: val.type,
                                department: val.department,
                            });
                        });
                        break;
                    case userTypes.group:
                        getDepartmentByCode(val.value, oppositeLang).then(res => {
                            let oppositeName = "";
                            if (res && res.groups && res.groups.result) {
                                const oppositeVal = res.groups.result.find((g) => g.slug === val.value);
                                if (oppositeVal) {
                                    oppositeName = oppositeVal.title;
                                }
                            }
                            onChange({
                                login: val.value,
                                name: oppositeLang === "en" ? val.label : oppositeName,
                                nameEn: oppositeLang === "en" ? oppositeName : val.label,
                                type: val.type,
                                department: val.department,
                            });
                        });
                        break;
                    case userTypes.all:
                        onChange({
                            login: val.value,
                            name: "Все",
                            nameEn: "All",
                            type: val.type,
                            department: val.department,
                        });
                        break;
                }
            }
        }
    }

    return (<AsyncSearchableSelect
            formatOptionLabel={formatOptionLabel}
            placeholder={placeholder}
            showSelected={showSelected}
            value={selectValue}
            disabled={disabled}
            name={name}
            loadOptions={loadOptions}
            isMulti={isMulti}
            onChange={handleChange}
        />
    );
};


